import { createApi } from '@reduxjs/toolkit/query/react'
import { original } from '@reduxjs/toolkit'
import { BACKEND_API_URL, CURSOR_LIST_PAGE_SIZE, urlApi } from '../consts/api'
import { appendCommentsSortQueryParams, DEFAULT_COMMENTS_SORT } from '../components/CommentsSection/helpers/commentsSortQuery'
import { patchCommentNodePreserveChildren } from './commentTreePatch'
import { extractResponseData, extractResponseError, fetchBaseQueryWithToken } from '../utils/api'
import { getMediaSourceUrl } from '../utils/media'
import type {
  CommentsSortUi,
  IAddCommentToProject,
  IGetCommentResponse,
  IProjectCommentCountersByType,
  ISetCommentReactionBody,
  IUpdateCommentBody,
  TProjectsCursorPage,
} from './types/projects'
import { parseProjectsCursorPageResponse } from './projects'
import {
  resolveSocialOrderCommentsTotal,
  type GetSocialOrderCommentThreadPageArg,
  type GetSocialOrderCommentsQueryArg,
  type ISocialOrderDetail,
  type ISocialOrderLikeUserResource,
  type ISocialOrderListItem,
  type ISocialOrderModerationUserCandidate,
  type ISocialOrderModerationUserCandidatesResponse,
  type ISocialOrderResource,
  type ISocialOrderUserRestrictionResource,
  type SocialOrderUserRestrictionLevel,
  type TCreateSocialOrderBody,
  type TGetSocialOrderCommentsResponse,
  type TGetSocialOrdersCursorQueryParams,
  type TGetSocialOrdersQueryParams,
  type TSocialOrderCommentThreadPage,
  type TSocialOrdersCursorPage,
  type TSocialOrdersListResponse,
  type TUpdateSocialOrderBody,
} from './types/socialOrders'

export const socialOrdersTag = 'socialOrdersTag' as const
const LIKES_PREVIEW_MAX = 3

const SOCIAL_ORDER_LIST_ENDPOINTS = new Set([
  'getSocialOrdersPage',
  'getMySocialOrdersPage',
  'getSubscribedSocialOrdersPage',
  'getUserSocialOrdersPage',
])

type TSocialOrderMutationCacheArg = {
  socialOrderId: number
  cacheKey?: number | string
}

type TSocialOrderLikeCountersDraft = {
  viewer?: { is_liked?: boolean } | null
  counters: { likes?: number }
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === 'object' && !Array.isArray(value)
}

function normalizeUserShort(raw: unknown): unknown {
  if (!isRecord(raw)) return raw
  const avatarRaw = raw.avatar ?? raw.avatar_url ?? raw.avatarUrl
  return {
    ...raw,
    avatar: getMediaSourceUrl(avatarRaw),
  }
}

function normalizeCommentMyReaction(raw: unknown): number | null | undefined {
  if (raw === undefined) return undefined
  if (raw === null) return null
  if (raw === 'like') return 1
  if (raw === 'dislike') return -1
  const n = Number(raw)
  return Number.isFinite(n) ? n : null
}

function normalizeCommentNode(raw: unknown): IGetCommentResponse {
  if (raw == null || typeof raw !== 'object') {
    return raw as IGetCommentResponse
  }
  const c = raw as Record<string, unknown>
  const childrenRaw = c.children

  return {
    ...c,
    author: normalizeUserShort(c.author),
    my_reaction: normalizeCommentMyReaction(c.my_reaction),
    viewer: c.viewer && typeof c.viewer === 'object'
      ? { can_delete: (c.viewer as Record<string, unknown>).can_delete === true }
      : undefined,
    children: Array.isArray(childrenRaw)
      ? (childrenRaw as unknown[]).map((child) => normalizeCommentNode(child))
      : (childrenRaw as IGetCommentResponse['children']),
  } as IGetCommentResponse
}

function applySocialOrderLegacyCompat(resource: ISocialOrderResource): ISocialOrderDetail {
  const author = resource.author
  const fullname = String(author?.fullname ?? '').trim()
  const parts = fullname.split(/\s+/).filter(Boolean)
  const shortname = String(author?.shortname ?? '').trim()

  return {
    ...resource,
    authorId: resource.author_id,
    authorFirstName: parts[0] ?? (shortname ? shortname[0] : ''),
    authorLastName: parts[1] ?? '',
    authorOtch: parts[2] ?? '',
    authorAvatarUrl: String(author?.avatar ?? ''),
    authorCharacteristics: [],
    authorIsOnline: author?.is_online === true,
    createdAt: resource.created_at,
    updatedAt: resource.updated_at,
    views: Number(resource.counters.views ?? 0),
    likes: Number(resource.counters.likes ?? 0),
    comments: resolveSocialOrderCommentsTotal(resource.counters.comments),
  }
}

function normalizeSocialOrder(raw: unknown): ISocialOrderResource {
  const orderRaw = isRecord(raw) ? raw : {}
  const countersRaw = isRecord(orderRaw.counters) ? orderRaw.counters : {}
  const viewerRaw = isRecord(orderRaw.viewer) ? orderRaw.viewer : null
  const author = orderRaw.author ? normalizeUserShort(orderRaw.author) : orderRaw.author
  const likesPreview = Array.isArray(orderRaw.likes_preview)
    ? orderRaw.likes_preview.map((u) => normalizeUserShort(u))
    : orderRaw.likes_preview
  const commentsRaw = countersRaw.comments
  const imageGallery = Array.isArray(orderRaw.image_gallery)
    ? (orderRaw.image_gallery as unknown[])
        .map((item) => getMediaSourceUrl(item))
        .filter((url): url is string => Boolean(url))
    : undefined

  return {
    ...orderRaw,
    image: getMediaSourceUrl(orderRaw.image),
    image_gallery: imageGallery,
    author,
    likes_preview: likesPreview ?? null,
    is_followed: Boolean(orderRaw.is_followed ?? viewerRaw?.is_followed),
    is_completed: Boolean(orderRaw.is_completed),
    problem: orderRaw.problem == null || orderRaw.problem === '' ? null : String(orderRaw.problem),
    completion_description:
      orderRaw.completion_description == null || orderRaw.completion_description === ''
        ? null
        : String(orderRaw.completion_description),
    completion_resource_url:
      orderRaw.completion_resource_url == null || orderRaw.completion_resource_url === ''
        ? null
        : String(orderRaw.completion_resource_url),
    viewer: viewerRaw
      ? {
          is_followed: Boolean(viewerRaw.is_followed),
          is_liked: viewerRaw.is_liked === true,
          permissions: viewerRaw.permissions,
        }
      : null,
    counters: {
      likes: Number(countersRaw.likes ?? 0),
      views: Number(countersRaw.views ?? 0),
      subscribers: Number(countersRaw.subscribers ?? 0),
      linked_projects: Number(countersRaw.linked_projects ?? 0),
      comments:
        isRecord(commentsRaw)
          ? {
              total: Number(commentsRaw.total ?? 0),
              opinions: Number(commentsRaw.opinions ?? 0),
              suggestions: Number(commentsRaw.suggestions ?? 0),
              questions: Number(commentsRaw.questions ?? 0),
            }
          : Number(commentsRaw ?? 0),
    },
  } as ISocialOrderResource
}

function normalizeSocialOrderListItem(raw: unknown): ISocialOrderListItem {
  const item = normalizeSocialOrder(raw) as ISocialOrderListItem
  if (typeof item.counters.comments !== 'number') {
    item.counters.comments = Number((item.counters.comments as IProjectCommentCountersByType).total ?? 0)
  }
  return item
}

function parseSocialOrdersListResponse(response: unknown): TSocialOrdersListResponse {
  const payload = extractResponseData(response)
  if (Array.isArray(payload)) {
    return payload.map((item) => normalizeSocialOrderListItem(item))
  }
  if (payload && typeof payload === 'object' && Array.isArray((payload as { data?: unknown }).data)) {
    return (payload as { data: unknown[] }).data.map((item) => normalizeSocialOrderListItem(item))
  }
  return []
}

function pickCursorFromNextLink(next: string): string | null {
  try {
    const fromUrl = new URL(next, BACKEND_API_URL).searchParams.get('cursor')
    if (fromUrl) return fromUrl
  } catch {
    // fall through
  }
  const match = next.match(/[?&]cursor=([^&]+)/)
  return match?.[1] ? decodeURIComponent(match[1]) : null
}

function pickNextCursorFromPageRoot(body: Record<string, unknown>): string | null {
  const meta = body.meta as Record<string, unknown> | undefined
  const fromMeta = meta?.next_cursor
  if (fromMeta !== undefined && fromMeta !== null && String(fromMeta) !== '') {
    return String(fromMeta)
  }
  const links = body.links as Record<string, unknown> | undefined
  const next = links?.next
  if (typeof next === 'string' && next.length > 0) {
    return pickCursorFromNextLink(next)
  }
  return null
}

function parseSocialOrdersCursorPageResponse(response: unknown): TSocialOrdersCursorPage {
  const top = (response ?? {}) as Record<string, unknown>
  return {
    items: parseSocialOrdersListResponse(response),
    meta: { next_cursor: pickNextCursorFromPageRoot(top) },
  }
}

function buildSocialOrdersListUrl(basePath: string, params: TGetSocialOrdersCursorQueryParams | void): string {
  const raw = params ?? {}
  const search = new URLSearchParams()
  const q = raw.query?.trim()
  if (q) search.set('query', q)
  if (raw.cursor) search.set('cursor', raw.cursor)
  if (typeof raw.limit === 'number' && Number.isFinite(raw.limit)) search.set('limit', String(raw.limit))
  if (raw.sort_by) search.set('sort_by', raw.sort_by)
  if (raw.sort_direction) search.set('sort_direction', raw.sort_direction)
  const focus = Array.isArray(raw.focus) ? raw.focus.filter((f) => String(f ?? '').trim()) : []
  for (const value of focus) {
    search.append('focus[]', String(value).trim())
  }
  const qs = search.toString()
  return qs ? `${basePath}?${qs}` : basePath
}

function socialOrderListProvidesTags(
  result: TSocialOrdersListResponse | TSocialOrdersCursorPage | undefined,
): Array<{ type: typeof socialOrdersTag; id: string }> {
  if (!result) {
    return [{ type: socialOrdersTag, id: 'LIST' }]
  }
  const items = Array.isArray(result) ? result : result.items ?? []
  return [
    { type: socialOrdersTag, id: 'LIST' },
    ...items.map((order) => ({ type: socialOrdersTag, id: `id:${order.id}` })),
  ]
}

function socialOrderInvalidationTags(socialOrderId: number) {
  return [
    { type: socialOrdersTag, id: 'LIST' },
    { type: socialOrdersTag, id: `id:${socialOrderId}` },
  ]
}

/** Cross-API: project create/update/delete with social_order link → refresh SO counter + projects tab. */
export function invalidateSocialOrderLinkedProjectsCache(
  dispatch: (action: unknown) => unknown,
  socialOrderId: number | null | undefined,
): void {
  const id = Number(socialOrderId)
  if (!Number.isFinite(id) || id <= 0) return
  dispatch(
    socialOrdersApi.util.invalidateTags([
      { type: socialOrdersTag, id: `id:${id}` },
      { type: socialOrdersTag, id: `detail:${id}` },
      { type: socialOrdersTag, id: `projects:${id}` },
    ]),
  )
}

function socialOrderLikeListInvalidationTags(): Array<{ type: typeof socialOrdersTag; id: string }> {
  return [{ type: socialOrdersTag, id: 'LIST' }]
}

function patchSocialOrderDetail(
  dispatch: (action: unknown) => { undo: () => void },
  cacheKey: number | string | undefined,
  patcher: (draft: ISocialOrderResource) => void,
): { undo: () => void } | null {
  if (cacheKey == null) return null
  return dispatch(
    socialOrdersApi.util.updateQueryData('getSocialOrder', { id: cacheKey }, (draft) => {
      if (draft) patcher(draft)
    }),
  ) as { undo: () => void }
}

function patchSocialOrderInListCaches(
  dispatch: (action: unknown) => { undo: () => void },
  getState: () => unknown,
  socialOrderId: number,
  patcher: (draft: ISocialOrderListItem) => void,
): Array<{ undo: () => void }> {
  const patches: Array<{ undo: () => void }> = []
  const state = getState() as {
    socialOrdersApi?: {
      queries?: Record<string, { endpointName?: string; originalArgs?: unknown; data?: unknown }>
    }
  }
  const queries = state.socialOrdersApi?.queries ?? {}
  for (const queryEntry of Object.values(queries)) {
    const endpointName = queryEntry?.endpointName
    if (!endpointName || !SOCIAL_ORDER_LIST_ENDPOINTS.has(endpointName) || queryEntry.data == null) continue
    const args = queryEntry.originalArgs
    const patch = dispatch(
      socialOrdersApi.util.updateQueryData(
        endpointName as 'getSocialOrdersPage',
        args as TGetSocialOrdersCursorQueryParams | void,
        (draft) => {
          const item = draft.items?.find((order) => order.id === socialOrderId)
          if (item) patcher(item)
        },
      ),
    )
    patches.push(patch)
  }
  return patches
}

function applySocialOrderLikeToggle(draft: TSocialOrderLikeCountersDraft): void {
  const wasLiked = draft.viewer?.is_liked === true
  if (draft.viewer) {
    draft.viewer.is_liked = !wasLiked
  }
  draft.counters.likes = Math.max(0, Number(draft.counters.likes ?? 0) + (wasLiked ? -1 : 1))
}

function parseSocialOrderLikesResponse(
  response: unknown,
): { data: ISocialOrderLikeUserResource[]; meta: { next_cursor: string | null } } {
  const raw = isRecord(response) ? response : {}
  const data = Array.isArray(raw.data) ? raw.data : []
  const meta = isRecord(raw.meta) ? raw.meta : {}
  return {
    data: data.map((u) => {
      const item = isRecord(u) ? u : {}
      return {
        ...item,
        avatar: getMediaSourceUrl(item.avatar),
      } as ISocialOrderLikeUserResource
    }),
    meta: {
      next_cursor: meta.next_cursor != null ? String(meta.next_cursor) : null,
    },
  }
}

function parseCommentsListResponse(response: unknown): TGetSocialOrderCommentsResponse {
  const payload = extractResponseData(response)
  if (Array.isArray(payload)) {
    return payload.map((item) => normalizeCommentNode(item))
  }
  if (payload && typeof payload === 'object' && Array.isArray((payload as { data?: unknown }).data)) {
    return (payload as { data: unknown[] }).data.map((item) => normalizeCommentNode(item))
  }
  return []
}

function extractCommentThreadRowsAndMetaRoot(body: unknown): {
  rows: unknown[]
  metaRoot: Record<string, unknown>
} {
  if (body === null || typeof body !== 'object') return { rows: [], metaRoot: {} }
  const top = body as Record<string, unknown>
  if (Array.isArray(top.data)) return { rows: top.data as unknown[], metaRoot: top }
  const inner = top.data
  if (inner !== null && typeof inner === 'object' && !Array.isArray(inner)) {
    const lr = inner as Record<string, unknown>
    if (Array.isArray(lr.data)) return { rows: lr.data as unknown[], metaRoot: lr }
  }
  const once = extractResponseData(body)
  if (Array.isArray(once)) return { rows: once, metaRoot: top }
  if (once && typeof once === 'object' && Array.isArray((once as Record<string, unknown>).data)) {
    const o = once as Record<string, unknown>
    return { rows: o.data as unknown[], metaRoot: o }
  }
  return { rows: [], metaRoot: top }
}

function pickNextCursorFromBody(body: Record<string, unknown>): string | null {
  const meta = body.meta as Record<string, unknown> | undefined
  const fromMeta =
    meta?.next_cursor !== undefined && meta?.next_cursor !== null && meta.next_cursor !== ''
      ? String(meta.next_cursor)
      : ''
  if (fromMeta.length > 0) return fromMeta
  const links = body.links as Record<string, unknown> | undefined
  const next = links?.next
  if (typeof next === 'string' && next.length > 0) {
    try {
      const u = new URL(next, BACKEND_API_URL)
      return u.searchParams.get('cursor') ?? null
    } catch {
      return null
    }
  }
  return null
}

export const socialOrdersApi = createApi({
  reducerPath: 'socialOrdersApi',
  baseQuery: fetchBaseQueryWithToken(BACKEND_API_URL),
  tagTypes: [socialOrdersTag],
  endpoints: (builder) => ({
    getSocialOrdersPage: builder.query<TSocialOrdersCursorPage, TGetSocialOrdersCursorQueryParams | void>({
      query: (arg) => ({
        url: buildSocialOrdersListUrl(`/${urlApi.socialOrders}`, arg),
      }),
      transformResponse: (response: unknown) => parseSocialOrdersCursorPageResponse(response),
      transformErrorResponse: extractResponseError,
      providesTags: (result) => socialOrderListProvidesTags(result),
    }),

    getMySocialOrdersPage: builder.query<TSocialOrdersCursorPage, TGetSocialOrdersCursorQueryParams | void>({
      query: (arg) => ({
        url: buildSocialOrdersListUrl(`/${urlApi.socialOrders}/mine`, arg),
      }),
      transformResponse: (response: unknown) => parseSocialOrdersCursorPageResponse(response),
      transformErrorResponse: extractResponseError,
      providesTags: (result) => socialOrderListProvidesTags(result),
    }),

    getSubscribedSocialOrdersPage: builder.query<TSocialOrdersCursorPage, TGetSocialOrdersCursorQueryParams | void>({
      query: (arg) => ({
        url: buildSocialOrdersListUrl(`/${urlApi.socialOrders}/subscriptions`, arg),
      }),
      transformResponse: (response: unknown) => parseSocialOrdersCursorPageResponse(response),
      transformErrorResponse: extractResponseError,
      providesTags: (result) => socialOrderListProvidesTags(result),
    }),

    getUserSocialOrdersPage: builder.query<
      TSocialOrdersCursorPage,
      TGetSocialOrdersCursorQueryParams & { userId: number }
    >({
      query: ({ userId, ...arg }) => ({
        url: buildSocialOrdersListUrl(`/${urlApi.users}/${userId}/social-orders`, arg),
      }),
      transformResponse: (response: unknown) => parseSocialOrdersCursorPageResponse(response),
      transformErrorResponse: extractResponseError,
      providesTags: (result) => socialOrderListProvidesTags(result),
    }),

    getSocialOrder: builder.query<ISocialOrderDetail | null, { id: number | string }>({
      query: ({ id }) => ({
        url: `/${urlApi.socialOrders}/${id}`,
      }),
      transformResponse: (response: unknown) => {
        const payload = extractResponseData(response)
        if (!payload || typeof payload !== 'object') return null
        return applySocialOrderLegacyCompat(normalizeSocialOrder(payload))
      },
      transformErrorResponse: extractResponseError,
      providesTags: (result) =>
        result
          ? [
              { type: socialOrdersTag, id: `id:${result.id}` },
              { type: socialOrdersTag, id: `detail:${result.id}` },
            ]
          : [{ type: socialOrdersTag, id: 'LIST' }],
    }),

    getSocialOrderProjectsPage: builder.query<
      TProjectsCursorPage,
      { socialOrderId: number; cursor?: string | null; limit?: number }
    >({
      query: ({ socialOrderId, cursor, limit }) => ({
        url: `/${urlApi.socialOrders}/${socialOrderId}/projects`,
        params: {
          ...(cursor ? { cursor } : {}),
          ...(limit != null ? { limit: String(limit) } : {}),
        },
      }),
      transformResponse: (response: unknown) => parseProjectsCursorPageResponse(response),
      transformErrorResponse: extractResponseError,
      providesTags: (_result, _error, arg) => [
        { type: socialOrdersTag, id: `projects:${arg.socialOrderId}` },
      ],
    }),

    createSocialOrder: builder.mutation<ISocialOrderResource, { payload: TCreateSocialOrderBody }>({
      query: ({ payload }) => ({
        url: `/${urlApi.socialOrders}`,
        method: 'POST',
        body: payload,
      }),
      transformResponse: (response: unknown) => normalizeSocialOrder(extractResponseData(response)),
      transformErrorResponse: extractResponseError,
      invalidatesTags: [{ type: socialOrdersTag, id: 'LIST' }],
    }),

    createSocialOrderDraft: builder.mutation<{ id: number }, { payload: TCreateSocialOrderBody }>({
      query: ({ payload }) => ({
        url: `/${urlApi.socialOrders}`,
        method: 'POST',
        body: payload,
      }),
      transformResponse: (response: unknown) => {
        const normalized = normalizeSocialOrder(extractResponseData(response))
        return { id: normalized.id }
      },
      transformErrorResponse: extractResponseError,
      invalidatesTags: [{ type: socialOrdersTag, id: 'LIST' }],
    }),

    updateSocialOrder: builder.mutation<ISocialOrderResource, { id: number; payload: TUpdateSocialOrderBody }>({
      query: ({ id, payload }) => ({
        url: `/${urlApi.socialOrders}/${id}/update`,
        method: 'PUT',
        body: payload,
      }),
      transformResponse: (response: unknown) => normalizeSocialOrder(extractResponseData(response)),
      transformErrorResponse: extractResponseError,
      invalidatesTags: (result, _error, arg) => socialOrderInvalidationTags(result?.id ?? arg.id),
    }),

    deleteSocialOrder: builder.mutation<void, { socialOrderId: number }>({
      query: ({ socialOrderId }) => ({
        url: `/${urlApi.socialOrders}/${socialOrderId}/delete`,
        method: 'DELETE',
      }),
      transformErrorResponse: extractResponseError,
      invalidatesTags: (_result, _error, arg) => socialOrderInvalidationTags(arg.socialOrderId),
    }),

    uploadSocialOrderPreview: builder.mutation<unknown, { socialOrderId: number; file: File }>({
      query: ({ socialOrderId, file }) => {
        const formData = new FormData()
        formData.append('image', file)
        return {
          url: `/${urlApi.socialOrders}/${socialOrderId}/preview`,
          method: 'POST',
          body: formData,
        }
      },
      transformResponse: (response: unknown) => extractResponseData(response),
      transformErrorResponse: extractResponseError,
      invalidatesTags: (_result, _error, arg) => socialOrderInvalidationTags(arg.socialOrderId),
    }),

    deleteSocialOrderPreview: builder.mutation<void, { socialOrderId: number }>({
      query: ({ socialOrderId }) => ({
        url: `/${urlApi.socialOrders}/${socialOrderId}/preview`,
        method: 'DELETE',
      }),
      transformErrorResponse: extractResponseError,
      invalidatesTags: (_result, _error, arg) => socialOrderInvalidationTags(arg.socialOrderId),
    }),

    getSocialOrderImages: builder.query<
      { coverUrl: string | null; images: { url: string; mediaId?: number }[] },
      { socialOrderId: number }
    >({
      query: ({ socialOrderId }) => ({
        url: `/${urlApi.socialOrders}/${socialOrderId}`,
      }),
      transformResponse: (response: unknown) => {
        const data: any = extractResponseData(response)
        const coverUrl = getMediaSourceUrl(data?.image) || null
        const images = Array.isArray(data?.image_gallery)
          ? (data.image_gallery as unknown[]).flatMap((x: any) => {
              const url = getMediaSourceUrl(x)
              if (!url) return []
              const mediaId = x?.id != null ? Number(x.id) : undefined
              return [mediaId != null ? { url, mediaId } : { url }]
            })
          : []
        return { coverUrl, images }
      },
      transformErrorResponse: extractResponseError,
      providesTags: (_result, _error, arg) => [
        { type: socialOrdersTag, id: `id:${arg.socialOrderId}` },
      ],
    }),

    uploadSocialOrderGallery: builder.mutation<unknown[], { socialOrderId: number; files: File[] }>({
      query: ({ socialOrderId, files }) => {
        const formData = new FormData()
        files.forEach((file) => formData.append('images[]', file))
        return {
          url: `/${urlApi.socialOrders}/${socialOrderId}/gallery`,
          method: 'POST',
          body: formData,
        }
      },
      transformResponse: (response: unknown) => {
        const data = extractResponseData(response)
        if (Array.isArray(data)) return data
        return Array.isArray(response) ? (response as unknown[]) : []
      },
      transformErrorResponse: extractResponseError,
      invalidatesTags: (_result, _error, arg) => socialOrderInvalidationTags(arg.socialOrderId),
    }),

    deleteSocialOrderGalleryImage: builder.mutation<void, { socialOrderId: number; mediaId: number }>({
      query: ({ socialOrderId, mediaId }) => ({
        url: `/${urlApi.socialOrders}/${socialOrderId}/gallery/${mediaId}`,
        method: 'DELETE',
      }),
      transformErrorResponse: extractResponseError,
      invalidatesTags: (_result, _error, arg) => socialOrderInvalidationTags(arg.socialOrderId),
    }),

    trackSocialOrderView: builder.mutation<{ views: number }, { socialOrderId: number; cacheKey?: number | string }>({
      query: ({ socialOrderId }) => ({
        url: `/${urlApi.socialOrders}/${socialOrderId}/view`,
        method: 'POST',
      }),
      transformResponse: (response: unknown) => {
        const data = extractResponseData(response) as { views?: number } | null
        return { views: Number(data?.views ?? 0) }
      },
      transformErrorResponse: extractResponseError,
      async onQueryStarted(arg, { dispatch, queryFulfilled, getState }) {
        try {
          const { data } = await queryFulfilled
          if (arg.cacheKey != null) {
            patchSocialOrderDetail(dispatch, arg.cacheKey, (draft) => {
              draft.counters.views = data.views
            })
          }
          patchSocialOrderInListCaches(dispatch, getState, arg.socialOrderId, (draft) => {
            draft.counters.views = data.views
          })
        } catch {
          // view tracking should not disturb the page
        }
      },
    }),

    subscribeSocialOrder: builder.mutation<unknown, TSocialOrderMutationCacheArg>({
      query: ({ socialOrderId }) => ({
        url: `/${urlApi.socialOrders}/${socialOrderId}/subscribe`,
        method: 'POST',
      }),
      transformResponse: (response: unknown) => extractResponseData(response),
      transformErrorResponse: extractResponseError,
      invalidatesTags: (_result, _error, arg) => socialOrderInvalidationTags(arg.socialOrderId),
      async onQueryStarted(arg, { dispatch, queryFulfilled }) {
        const detailPatch = patchSocialOrderDetail(dispatch, arg.cacheKey, (draft) => {
          if (draft.viewer) draft.viewer.is_followed = true
          draft.is_followed = true
          draft.counters.subscribers = Number(draft.counters.subscribers ?? 0) + 1
        })
        try {
          await queryFulfilled
        } catch {
          detailPatch?.undo()
        }
      },
    }),

    unsubscribeSocialOrder: builder.mutation<unknown, TSocialOrderMutationCacheArg>({
      query: ({ socialOrderId }) => ({
        url: `/${urlApi.socialOrders}/${socialOrderId}/subscribe`,
        method: 'DELETE',
      }),
      transformResponse: (response: unknown) => extractResponseData(response),
      transformErrorResponse: extractResponseError,
      invalidatesTags: (_result, _error, arg) => socialOrderInvalidationTags(arg.socialOrderId),
      async onQueryStarted(arg, { dispatch, queryFulfilled }) {
        const detailPatch = patchSocialOrderDetail(dispatch, arg.cacheKey, (draft) => {
          if (draft.viewer) draft.viewer.is_followed = false
          draft.is_followed = false
          draft.counters.subscribers = Math.max(0, Number(draft.counters.subscribers ?? 0) - 1)
        })
        try {
          await queryFulfilled
        } catch {
          detailPatch?.undo()
        }
      },
    }),

    likeSocialOrder: builder.mutation<unknown, TSocialOrderMutationCacheArg>({
      query: ({ socialOrderId }) => ({
        url: `/${urlApi.socialOrders}/${socialOrderId}/like`,
        method: 'POST',
      }),
      transformResponse: (response: unknown) => extractResponseData(response),
      transformErrorResponse: extractResponseError,
      invalidatesTags: () => socialOrderLikeListInvalidationTags(),
      async onQueryStarted(arg, { dispatch, queryFulfilled, getState }) {
        const detailPatch = patchSocialOrderDetail(dispatch, arg.cacheKey, applySocialOrderLikeToggle)
        const listPatches = patchSocialOrderInListCaches(dispatch, getState, arg.socialOrderId, applySocialOrderLikeToggle)
        try {
          await queryFulfilled
          if (arg.cacheKey != null) {
            const likesPreviewResult = await dispatch(
              socialOrdersApi.endpoints.getSocialOrderLikes.initiate(
                { socialOrderId: arg.socialOrderId, limit: LIKES_PREVIEW_MAX, cursor: null },
                { forceRefetch: true },
              ),
            ).unwrap()
            patchSocialOrderDetail(dispatch, arg.cacheKey, (draft) => {
              draft.likes_preview = (likesPreviewResult.data ?? []).slice(0, LIKES_PREVIEW_MAX)
            })
          }
        } catch {
          detailPatch?.undo()
          listPatches.forEach((p) => p.undo())
        }
      },
    }),

    unlikeSocialOrder: builder.mutation<unknown, TSocialOrderMutationCacheArg>({
      query: ({ socialOrderId }) => ({
        url: `/${urlApi.socialOrders}/${socialOrderId}/like`,
        method: 'DELETE',
      }),
      transformResponse: (response: unknown) => extractResponseData(response),
      transformErrorResponse: extractResponseError,
      invalidatesTags: () => socialOrderLikeListInvalidationTags(),
      async onQueryStarted(arg, { dispatch, queryFulfilled, getState }) {
        const detailPatch = patchSocialOrderDetail(dispatch, arg.cacheKey, applySocialOrderLikeToggle)
        const listPatches = patchSocialOrderInListCaches(dispatch, getState, arg.socialOrderId, applySocialOrderLikeToggle)
        try {
          await queryFulfilled
          if (arg.cacheKey != null) {
            const likesPreviewResult = await dispatch(
              socialOrdersApi.endpoints.getSocialOrderLikes.initiate(
                { socialOrderId: arg.socialOrderId, limit: LIKES_PREVIEW_MAX, cursor: null },
                { forceRefetch: true },
              ),
            ).unwrap()
            patchSocialOrderDetail(dispatch, arg.cacheKey, (draft) => {
              draft.likes_preview = (likesPreviewResult.data ?? []).slice(0, LIKES_PREVIEW_MAX)
            })
          }
        } catch {
          detailPatch?.undo()
          listPatches.forEach((p) => p.undo())
        }
      },
    }),

    getSocialOrderLikes: builder.query<
      { data: ISocialOrderLikeUserResource[]; meta: { next_cursor: string | null } },
      { socialOrderId: number; limit?: number; cursor?: string | null }
    >({
      query: ({ socialOrderId, limit, cursor }) => ({
        url: `/${urlApi.socialOrders}/${socialOrderId}/likes`,
        params: {
          ...(limit != null ? { limit: String(limit) } : {}),
          ...(cursor ? { cursor } : {}),
        },
      }),
      transformResponse: (response: unknown) => parseSocialOrderLikesResponse(response),
      transformErrorResponse: extractResponseError,
      providesTags: (_result, _error, arg) => [{ type: socialOrdersTag, id: `likes:${arg.socialOrderId}` }],
    }),

    getSocialOrderComments: builder.query<TGetSocialOrderCommentsResponse, GetSocialOrderCommentsQueryArg>({
      query: ({ socialOrderId, type, parentId, limit, cursor, sort }) => {
        const params: Record<string, string> = {}
        if (parentId != null) params.parent_id = String(parentId)
        if (limit != null) params.limit = String(limit)
        if (cursor != null && cursor !== '') params.cursor = String(cursor)
        if (parentId == null) {
          appendCommentsSortQueryParams(params, sort ?? DEFAULT_COMMENTS_SORT)
        }
        return {
          url: `/${urlApi.socialOrders}/${socialOrderId}/comments/${type ? type : ''}`,
          ...(Object.keys(params).length > 0 ? { params } : {}),
        }
      },
      transformResponse: (response: unknown): TGetSocialOrderCommentsResponse => parseCommentsListResponse(response),
      transformErrorResponse: extractResponseError,
      providesTags: [socialOrdersTag],
    }),

    getSocialOrderCommentThreadPage: builder.query<TSocialOrderCommentThreadPage, GetSocialOrderCommentThreadPageArg>({
      async queryFn(arg, _api, _extraOptions, baseQuery) {
        const params: Record<string, string> = {
          parent_id: String(arg.parentId),
        }
        if (arg.cursor) params.cursor = arg.cursor
        if (arg.limit != null) params.limit = String(arg.limit)

        const res = await baseQuery({
          url: `/${urlApi.socialOrders}/${arg.socialOrderId}/comments/${arg.type ? arg.type : ''}`,
          params,
        })
        if (res.error) return { error: res.error }
        const { rows, metaRoot } = extractCommentThreadRowsAndMetaRoot(res.data)
        const items = rows.map((item) => normalizeCommentNode(item))
        const nextCursor = pickNextCursorFromBody(metaRoot)
        return { data: { items, nextCursor } }
      },
    }),

    addCommentToSocialOrder: builder.mutation<
      IGetCommentResponse,
      { socialOrderId: number; type: string; body: IAddCommentToProject }
    >({
      query: ({ socialOrderId, type, body }) => ({
        url: `/${urlApi.socialOrders}/${socialOrderId}/comments/${type ? type : ''}`,
        method: 'POST',
        body,
      }),
      transformResponse: (response: unknown) => normalizeCommentNode(extractResponseData(response)),
      transformErrorResponse: extractResponseError,
    }),

    updateSocialOrderComment: builder.mutation<IGetCommentResponse, { commentId: number; body: IUpdateCommentBody }>({
      query: ({ commentId, body }) => ({
        url: `/${urlApi.comments}/${commentId}/update`,
        method: 'PUT',
        body,
      }),
      transformResponse: (response: unknown) => normalizeCommentNode(extractResponseData(response)),
      transformErrorResponse: extractResponseError,
    }),

    setSocialOrderCommentReaction: builder.mutation<
      IGetCommentResponse,
      {
        commentId: number
        body: ISetCommentReactionBody
        cache?: {
          kind: 'socialOrder'
          socialOrderId: number
          type: string
          sort?: CommentsSortUi
          threadParentId?: number
        }
      }
    >({
      query: ({ commentId, body }) => ({
        url: `/${urlApi.comments}/${commentId}/reaction`,
        method: 'PUT',
        body,
      }),
      transformResponse: (response: unknown) => normalizeCommentNode(extractResponseData(response)),
      transformErrorResponse: extractResponseError,
      async onQueryStarted(arg, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled
          if (!arg.cache) return
          const normalized = normalizeCommentNode(data)
          dispatch(
            socialOrdersApi.util.updateQueryData(
              'getSocialOrderComments',
              {
                socialOrderId: arg.cache.socialOrderId,
                type: arg.cache.type,
                limit: CURSOR_LIST_PAGE_SIZE,
                sort: arg.cache.sort ?? DEFAULT_COMMENTS_SORT,
              },
              (draft) => patchCommentNodePreserveChildren(original(draft) ?? [...draft], arg.commentId, normalized),
            ),
          )
          if (arg.cache.threadParentId != null) {
            dispatch(
              socialOrdersApi.util.updateQueryData(
                'getSocialOrderCommentThreadPage',
                {
                  socialOrderId: arg.cache.socialOrderId,
                  type: arg.cache.type,
                  parentId: arg.cache.threadParentId,
                  limit: CURSOR_LIST_PAGE_SIZE,
                  cursor: null,
                },
                (draft) => {
                  draft.items = patchCommentNodePreserveChildren(draft.items ?? [], arg.commentId, normalized)
                },
              ),
            )
          }
        } catch {
          // ignore
        }
      },
    }),

    deleteSocialOrderComment: builder.mutation<void, { commentId: number }>({
      query: ({ commentId }) => ({
        url: `/${urlApi.comments}/${commentId}/delete`,
        method: 'DELETE',
      }),
      transformErrorResponse: extractResponseError,
    }),

    getSocialOrderModerationUserCandidates: builder.query<
      ISocialOrderModerationUserCandidatesResponse,
      { socialOrderId: number }
    >({
      query: ({ socialOrderId }) => ({
        url: `/${urlApi.socialOrders}/${socialOrderId}/moderation/user-candidates`,
      }),
      transformResponse: (response: unknown): ISocialOrderModerationUserCandidatesResponse => {
        const root = response as { meta?: { total?: unknown } } | null
        const payload = extractResponseData(response)
        const totalRaw = root?.meta?.total
        const itemsRaw = Array.isArray(payload) ? payload : []
        const items: ISocialOrderModerationUserCandidate[] = []

        for (const item of itemsRaw as any[]) {
          const user = normalizeUserShort(item) as Record<string, unknown> | null
          if (!user || typeof user !== 'object') continue
          const id = Number(item?.user_id ?? user.id)
          if (!Number.isSafeInteger(id) || id <= 0) continue

          items.push({
            id,
            user_id: id,
            slug: user.slug != null ? String(user.slug) : null,
            fullname: user.fullname != null ? String(user.fullname) : null,
            shortname: user.shortname != null ? String(user.shortname) : null,
            avatar: user.avatar != null ? String(user.avatar) : null,
            deleted: Boolean(user.deleted),
            is_online: user.is_online === true,
            restrictable: Boolean(item?.restrictable ?? true),
            restrictable_reason:
              typeof item?.restrictable_reason === 'string' ? item.restrictable_reason : null,
          })
        }

        const total =
          typeof totalRaw === 'number' && Number.isFinite(totalRaw) ? totalRaw : items.length
        return { items, total }
      },
      transformErrorResponse: extractResponseError,
      providesTags: (_result, _error, arg) => [
        { type: socialOrdersTag, id: `moderation-candidates:${arg.socialOrderId}` },
      ],
    }),

    getSocialOrderRestrictions: builder.query<
      ISocialOrderUserRestrictionResource[],
      { socialOrderId: number }
    >({
      query: ({ socialOrderId }) => ({
        url: `/${urlApi.socialOrders}/${socialOrderId}/restrictions`,
      }),
      transformResponse: (response: unknown) => {
        const payload = extractResponseData(response)
        if (!Array.isArray(payload)) return []
        return payload
          .map((item: any) => {
            const user = normalizeUserShort(item?.user) as ISocialOrderUserRestrictionResource['user'] | null
            const createdBy = normalizeUserShort(item?.created_by) as
              | ISocialOrderUserRestrictionResource['created_by']
              | null
            if (!user) return null
            const userId = Number(item?.user_id ?? user.id)
            if (!Number.isSafeInteger(userId) || userId <= 0) return null
            const level =
              item?.level === 'full' || item?.level === 'comments' ? item.level : 'comments'
            return {
              user,
              user_id: userId,
              level,
              reason: item?.reason != null ? String(item.reason) : null,
              created_at: String(item?.created_at ?? ''),
              created_by: createdBy ?? user,
            } satisfies ISocialOrderUserRestrictionResource
          })
          .filter((item): item is ISocialOrderUserRestrictionResource => item !== null)
      },
      transformErrorResponse: extractResponseError,
      providesTags: (_result, _error, arg) => [
        { type: socialOrdersTag, id: `restrictions:${arg.socialOrderId}` },
      ],
    }),

    createSocialOrderRestriction: builder.mutation<
      ISocialOrderUserRestrictionResource,
      {
        socialOrderId: number
        body: { user_id: number; level: SocialOrderUserRestrictionLevel; reason?: string }
      }
    >({
      query: ({ socialOrderId, body }) => ({
        url: `/${urlApi.socialOrders}/${socialOrderId}/restrictions`,
        method: 'POST',
        body,
      }),
      transformResponse: (response: unknown) => {
        const item = extractResponseData(response) as any
        const user = normalizeUserShort(item?.user) as ISocialOrderUserRestrictionResource['user'] | null
        const createdBy = normalizeUserShort(item?.created_by) as
          | ISocialOrderUserRestrictionResource['created_by']
          | null
        if (!user) {
          throw new Error('Invalid restriction response')
        }
        const userId = Number(item?.user_id ?? user.id)
        if (!Number.isSafeInteger(userId) || userId <= 0) {
          throw new Error('Invalid restriction user_id')
        }
        const level = item?.level === 'full' || item?.level === 'comments' ? item.level : 'comments'
        return {
          user,
          user_id: userId,
          level,
          reason: item?.reason != null ? String(item.reason) : null,
          created_at: String(item?.created_at ?? ''),
          created_by: createdBy ?? user,
        } satisfies ISocialOrderUserRestrictionResource
      },
      transformErrorResponse: extractResponseError,
      invalidatesTags: (_result, _error, arg) => [
        ...socialOrderInvalidationTags(arg.socialOrderId),
        { type: socialOrdersTag, id: `restrictions:${arg.socialOrderId}` },
        { type: socialOrdersTag, id: `moderation-candidates:${arg.socialOrderId}` },
      ],
    }),

    deleteSocialOrderRestriction: builder.mutation<void, { socialOrderId: number; userId: number }>({
      query: ({ socialOrderId, userId }) => ({
        url: `/${urlApi.socialOrders}/${socialOrderId}/restrictions/${userId}`,
        method: 'DELETE',
      }),
      transformErrorResponse: extractResponseError,
      invalidatesTags: (_result, _error, arg) => [
        ...socialOrderInvalidationTags(arg.socialOrderId),
        { type: socialOrdersTag, id: `restrictions:${arg.socialOrderId}` },
        { type: socialOrdersTag, id: `moderation-candidates:${arg.socialOrderId}` },
      ],
    }),
  }),
})

export const {
  useGetSocialOrdersPageQuery,
  useLazyGetSocialOrdersPageQuery,
  useGetMySocialOrdersPageQuery,
  useLazyGetMySocialOrdersPageQuery,
  useGetSubscribedSocialOrdersPageQuery,
  useLazyGetSubscribedSocialOrdersPageQuery,
  useLazyGetUserSocialOrdersPageQuery,
  useGetSocialOrderQuery,
  useLazyGetSocialOrderQuery,
  useGetSocialOrderProjectsPageQuery,
  useLazyGetSocialOrderProjectsPageQuery,
  useCreateSocialOrderMutation,
  useCreateSocialOrderDraftMutation,
  useUpdateSocialOrderMutation,
  useDeleteSocialOrderMutation,
  useUploadSocialOrderPreviewMutation,
  useDeleteSocialOrderPreviewMutation,
  useGetSocialOrderImagesQuery,
  useUploadSocialOrderGalleryMutation,
  useDeleteSocialOrderGalleryImageMutation,
  useTrackSocialOrderViewMutation,
  useSubscribeSocialOrderMutation,
  useUnsubscribeSocialOrderMutation,
  useLikeSocialOrderMutation,
  useUnlikeSocialOrderMutation,
  useGetSocialOrderLikesQuery,
  useLazyGetSocialOrderLikesQuery,
  useGetSocialOrderCommentsQuery,
  useLazyGetSocialOrderCommentsQuery,
  useGetSocialOrderCommentThreadPageQuery,
  useLazyGetSocialOrderCommentThreadPageQuery,
  useAddCommentToSocialOrderMutation,
  useUpdateSocialOrderCommentMutation,
  useSetSocialOrderCommentReactionMutation,
  useDeleteSocialOrderCommentMutation,
  useGetSocialOrderModerationUserCandidatesQuery,
  useGetSocialOrderRestrictionsQuery,
  useCreateSocialOrderRestrictionMutation,
  useDeleteSocialOrderRestrictionMutation,
} = socialOrdersApi

export type { TGetSocialOrdersQueryParams }
